import React, { useState, useEffect } from "react";
import { supabase } from "../lib/supabaseClient";

import {
  hashReportContent,
  importPublicKeyFromBase64,
  base64ToBuffer,
} from "../components/dashboardComponents/hashComponent";

import {
  Paper,
  Box,
  Button,
  Typography,
  TextField,
  Snackbar,
  MenuItem,
  FormControlLabel,
  Switch,
} from "@mui/material";
import DashboardLayout from "../components/DashboardLayout";

const VerifyPageContent = ({ user, onLogout }) => {
  // ----- Verify state -----
  const [reportId, setReportId] = useState("");
  const [orgId, setOrgId] = useState("");
  const [modelId, setModelId] = useState("");
  const [typeOfReport, setTypeOfReport] = useState("");
  const [postAnonymously, setPostAnonymously] = useState(false);
  const [publicKeyBase64, setPublicKeyBase64] = useState("");
  const [signatureBase64, setSignatureBase64] = useState("");

  const [pdfUrl, setPdfUrl] = useState(null);
  const [reportDetails, setReportDetails] = useState(null);
  const [result, setResult] = useState(null);
  const [snackOpen, setSnackOpen] = useState(false);

  const [models, setModels] = useState([]);
  const [orgs, setOrgs] = useState([]);

  // Load orgs
  useEffect(() => {
    const fetchOrganizations = async () => {
      const { data, error } = await supabase.from("organizations").select("*");
      if (!error) setOrgs(data || []);
    };
    fetchOrganizations();
  }, []);

  const handleOrgChange = async (e) => {
    const selected = e.target.value;
    setOrgId(selected);
    setModelId("");

    const { data, error } = await supabase
      .from("models")
      .select("id, name")
      .eq("org_id", selected);

    if (!error) setModels(data || []);
  };

  const handleVerify = async () => {
    const reportString = `${reportId}|${modelId}|${orgId}|${typeOfReport}`;
    const hashBuffer = await hashReportContent(reportString);

    // Fetch the report row INCLUDING the summary column
    const { data: report, error } = await supabase
      .from("issue_reports")
      .select("id, file_path, report_title, created_at, summary")
      .eq("report_id", reportId)
      .eq("model_id", modelId)
      .eq("org_id", orgId)
      .eq("type_of_report", typeOfReport)
      .maybeSingle();

    try {
      let isValid = true;

      if (postAnonymously) {
        const publicKey = await importPublicKeyFromBase64(publicKeyBase64);
        const signatureBuffer = base64ToBuffer(signatureBase64);
        isValid = await crypto.subtle.verify(
          { name: "RSASSA-PKCS1-v1_5" },
          publicKey,
          signatureBuffer,
          hashBuffer
        );
      }

      if (error || !report) {
        setResult("❌ Signature is invalid or report not found.");
      } else {
        setResult(
          isValid ? "✅ Signature is valid." : "❌ Signature is invalid."
        );
        if (isValid) {
          await handleSummary(
            report.file_path,
            report.report_title,
            report.created_at,
            report.summary || ""
          );
        }
      }
    } catch (err) {
      console.error(err);
      setResult("❌ Verification failed.");
    }

    setSnackOpen(true);
  };

  // Build PDF URL and store details (including DB summary)
  const handleSummary = async (filePath, title, createdAt, summaryText) => {
    const { data, error } = await supabase.storage
      .from("report-files")
      .getPublicUrl(filePath);

    if (error || !data?.publicUrl) {
      setResult("❌ Could not retrieve PDF file.");
      setSnackOpen(true);
      return;
    }

    setPdfUrl(data.publicUrl);
    setReportDetails({
      title: title || "Untitled",
      summary: summaryText || "",
      createdAt,
    });
  };

  const isVerified = result?.includes("✅");

  return (
    <Box component="main" sx={{ flexGrow: 1, p: 4 }}>
      <Box sx={{ maxWidth: 900, mx: "auto" }}>
        {!reportDetails && !pdfUrl && (
          <>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: "bold" }}>
              Verify a Report
            </Typography>

            <Paper
              elevation={4}
              sx={{
                p: 4,
                borderRadius: 3,
                display: "flex",
                flexDirection: "column",
                gap: 2,
                bgcolor: "background.default",
              }}
            >
              {/* Form Fields */}
              <TextField
                label="Report ID"
                fullWidth
                value={reportId}
                onChange={(e) => setReportId(e.target.value)}
              />

              <TextField
                select
                label="Select Company"
                value={orgId}
                onChange={handleOrgChange}
                fullWidth
                required
              >
                {orgs.map((org) => (
                  <MenuItem key={org.id} value={org.id}>
                    {org.name}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                label="Select Model"
                value={modelId}
                onChange={(e) => setModelId(e.target.value)}
                fullWidth
                required
                disabled={!orgId}
              >
                {models.map((model) => (
                  <MenuItem key={model.id} value={model.id}>
                    {model.name}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                label="Type of Report"
                value={typeOfReport}
                onChange={(e) => setTypeOfReport(e.target.value)}
                fullWidth
                required
              >
                {[
                  "Prompt Injection",
                  "Jailbreak",
                  "Ethics/Bias",
                  "Security Vulnerability",
                  "Evaluation Feedback",
                ].map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </TextField>

              <FormControlLabel
                control={
                  <Switch
                    checked={postAnonymously}
                    onChange={(e) => setPostAnonymously(e.target.checked)}
                  />
                }
                label="Submit anonymously"
              />

              {postAnonymously && (
                <>
                  <TextField
                    label="Public Key (Base64)"
                    fullWidth
                    multiline
                    minRows={3}
                    value={publicKeyBase64}
                    onChange={(e) => setPublicKeyBase64(e.target.value)}
                  />
                  <TextField
                    label="Signature (Base64)"
                    fullWidth
                    multiline
                    minRows={3}
                    value={signatureBase64}
                    onChange={(e) => setSignatureBase64(e.target.value)}
                  />
                </>
              )}

              <Button
                variant="contained"
                color="primary"
                onClick={handleVerify}
                sx={{ mt: 2 }}
              >
                Verify
              </Button>
            </Paper>
          </>
        )}

        {/* Report Details */}
        {reportDetails && (
          <Paper
            elevation={2}
            sx={{ mt: 4, p: 3, bgcolor: "background.default", borderRadius: 2 }}
          >
            <Typography variant="h6" gutterBottom>
              Report Details
            </Typography>
            <Typography>
              <strong>Title:</strong> {reportDetails.title}
            </Typography>

            {reportDetails.summary && (
              <Typography sx={{ mt: 1 }}>
                <strong>Summary:</strong> {reportDetails.summary}
              </Typography>
            )}

            <Typography sx={{ mt: 1 }}>
              <strong>Upload Date:</strong>{" "}
              {new Date(reportDetails.createdAt)
                .toUTCString()
                .replace("GMT", "UTC")}
            </Typography>
          </Paper>
        )}

        {/* Blurred PDF Preview */}
        {pdfUrl && (
          <Paper
            elevation={2}
            sx={{ mt: 4, p: 3, bgcolor: "background.default", borderRadius: 2 }}
          >
            <Typography variant="h6" gutterBottom>
              Blurred Preview
            </Typography>
            <iframe
              src={pdfUrl}
              style={{
                width: "100%",
                height: "500px",
                filter: "blur(6px)",
                pointerEvents: isVerified ? "auto" : "none",
                border: "none",
              }}
              title="Report PDF"
            />
          </Paper>
        )}
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackOpen}
        onClose={() => setSnackOpen(false)}
        message={result}
        autoHideDuration={4000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      />
    </Box>
  );
};

const VerifyPage = () => {
  return <DashboardLayout children={<VerifyPageContent />} />;
};

export default VerifyPage;
